import { DocumentParseResult } from '../interfaces/interface';
import { handleError, logger } from '../../../../logger';
import { EventsApiContextType } from '../../../context/EventsApiContextModels';
import { parseMeetingDataParser } from '../../../../api/meeting-events-service';
import { v4 as uuidv4 } from 'uuid';

// Use require for mammoth to avoid type issues (for HTML conversion with formatting)
const mammoth = require('mammoth');

export const ALLOWED_EXTENSIONS = ['.docx', '.jpg', '.jpeg', '.png', '.doc', '.dotx'] as const;
export const ALLOWED_EXTENSIONS_ACCEPT = ALLOWED_EXTENSIONS.join(',');
export const ALLOWED_EXTENSIONS_DISPLAY = ALLOWED_EXTENSIONS.map((ext) => ext.replace('.', '').toUpperCase()).join(' | ');

/**
 * Supported image MIME types for base64 conversion
 */
const SUPPORTED_IMAGE_MIME_TYPES: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'image/jpg': 'jpeg'
};

/**
 * Service for handling document processing operations
 */
export class DocumentService {
  /**
   * Parse a document (Word or Image) using API-based parsing service
   * @param file - The uploaded document file (DOCX, JPG, JPEG, PNG)
   * @param eventsApiContext - Events API context for real-time communication
   * @param userAlias - Current user's alias
   * @param customPrompt - Optional custom prompt for parsing
   * @returns Promise<DocumentParseResult> - Parsed meeting data with formatted HTML body
   */
  static async parseDocument(
    file: File,
    eventsApiContext: EventsApiContextType,
    userAlias: string,
    customPrompt?: string
  ): Promise<DocumentParseResult> {
    try {
      const defaultPrompt = 'Parse this EA Meeting Request Template and extract the meeting details.';

      // Validate file first
      const validation = this.validateFile(file, 10);
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.error!
        };
      }

      // Extract file format from extension
      const lastDotIndex = file.name.lastIndexOf('.');
      const fileFormat = lastDotIndex > -1 ? file.name.slice(lastDotIndex + 1).toLowerCase() : 'docx';
      const isImage = ['jpg', 'jpeg', 'png'].includes(fileFormat);

      let meetingData: string;
      let htmlContent: string = '';
      let imageData: string | undefined;

      // Generate session ID for the API request
      const sessionId = `meeting-parse-${uuidv4()}`;

      if (isImage) {
        // Convert image to base64 data URL format
        try {
          imageData = await this.convertFileToBase64DataUrl(file);
          logger.info('Image converted to base64', {
            fileName: file.name,
            fileType: file.type,
            base64Length: imageData.length
          });
          // For images, meetingData is not needed but we set a placeholder for API compatibility
          meetingData = 'See attached image for meeting details.';
        } catch (base64Error) {
          handleError('Failed to convert image to base64', base64Error);
          return {
            success: false,
            error: base64Error instanceof Error ? base64Error.message : 'Failed to convert image to base64'
          };
        }
      } else {
        // For DOCX: convert to HTML with table border styling
        const arrayBuffer = await file.arrayBuffer();

        // Configure mammoth options to preserve table formatting
        const mammothOptions = {
          arrayBuffer,
          styleMap: ['table => table.bordered-table', 'tr => tr.table-row', 'td => td.table-cell', 'th => th.table-header'].join('\n')
        };

        const htmlResult = await mammoth.convertToHtml(mammothOptions);
        htmlContent = htmlResult.value;

        // Post-process HTML to ensure all tables have borders
        htmlContent = this.addTableBorders(htmlContent);

        if (!htmlContent || htmlContent.trim().length === 0) {
          return {
            success: false,
            error: 'Document appears to be empty or could not be read'
          };
        }

        meetingData = htmlContent;
      }

      const apiResponse = await parseMeetingDataParser(
        sessionId,
        meetingData,
        customPrompt || defaultPrompt,
        eventsApiContext,
        userAlias,
        fileFormat,
        imageData // Pass base64 image data if available
      );

      if (!apiResponse.success || !apiResponse.response) {
        handleError('API parsing failed', new Error('Invalid response from parsing service'));
        return {
          success: false,
          error: 'API parsing failed: Invalid response from parsing service'
        };
      }

      // For images, backend returns HTML table in MeetingData field
      // For DOCX, use the mammoth-generated HTML content
      const finalHtmlContent = isImage ? apiResponse.response.MeetingData || '' : htmlContent;

      return {
        success: true,
        data: apiResponse.response,
        htmlContent: finalHtmlContent
      };
    } catch (error) {
      handleError('DocumentService: Error parsing document', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown parsing error'
      };
    }
  }

  /**
   * Add borders to all tables in HTML content
   * @param htmlContent - The HTML content to process
   * @returns HTML content with table borders added
   */
  static addTableBorders(htmlContent: string): string {
    try {
      // Add inline styles to ensure tables have borders
      let processedHtml = htmlContent;

      // Add borders to tables that don't already have them
      processedHtml = processedHtml.replace(
        /<table(?![^>]*style.*border)([^>]*)>/gi,
        '<table$1 style="border-collapse: collapse; border: 1px solid #000000ff; width: 100%;">'
      );

      // Add borders to table cells (td and th) that don't already have them
      processedHtml = processedHtml.replace(
        /<(td|th)(?![^>]*style.*border)([^>]*)>/gi,
        '<$1$2 style="border: 1px solid #000000ff; padding: 8px; text-align: left;">'
      );

      // Add class-based styling as well for mammoth's style mapping
      processedHtml = processedHtml.replace(
        /<table class="bordered-table"([^>]*)>/gi,
        '<table class="bordered-table"$1 style="border-collapse: collapse; border: 1px solid #000000ff; width: 100%;">'
      );

      processedHtml = processedHtml.replace(
        /<(td|th) class="table-(cell|header)"([^>]*)>/gi,
        '<$1 class="table-$2"$3 style="border: 1px solid #000000ff; padding: 8px; text-align: left;">'
      );

      return processedHtml;
    } catch (error) {
      handleError('DocumentService: Error adding table borders', error);
      return htmlContent;
    }
  }

  static validateFileType(file: File): { isValid: boolean; error?: string } {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'image/jpeg', // .jpg, .jpeg
      'image/png' // .png
    ];

    const lastDotIndex = file.name.lastIndexOf('.');
    const fileExtension = lastDotIndex > -1 ? file.name.slice(lastDotIndex).toLowerCase() : '';

    if (!allowedTypes.includes(file.type) && !(ALLOWED_EXTENSIONS as readonly string[]).includes(fileExtension)) {
      return {
        isValid: false,
        error: `Please upload supported file extensions ${ALLOWED_EXTENSIONS.join(', ')}`
      };
    }

    return { isValid: true };
  }

  static validateFileSize(file: File, maxSizeMB: number = 1): { isValid: boolean; error?: string } {
    const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes

    if (file.size > maxSizeBytes) {
      return {
        isValid: false,
        error: `File size must be less than ${(maxSizeMB * 1000).toFixed(2)}KB. Current file size: ${(file.size / 1000).toFixed(2)}KB`
      };
    }

    return { isValid: true };
  }

  static validateFile(file: File, maxSizeMB: number = 10): { isValid: boolean; error?: string } {
    const typeValidation = this.validateFileType(file);
    if (!typeValidation.isValid) {
      return typeValidation;
    }

    const sizeValidation = this.validateFileSize(file, maxSizeMB);
    if (!sizeValidation.isValid) {
      return sizeValidation;
    }

    return { isValid: true };
  }

  static extractTextFromHTML(htmlContent: string): string {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;

      return tempDiv.textContent || tempDiv.innerText || '';
    } catch (error) {
      handleError('DocumentService: Error extracting text from HTML', error);
      return htmlContent;
    }
  }

  static generateDocumentSummary(parseResult: DocumentParseResult): {
    hasSubject: boolean;
    requiredAttendeesCount: number;
    optionalAttendeesCount: number;
    totalAttendeesCount: number;
    hasContent: boolean;
    contentLength: number;
  } {
    if (!parseResult.success || !parseResult.data) {
      return {
        hasSubject: false,
        requiredAttendeesCount: 0,
        optionalAttendeesCount: 0,
        totalAttendeesCount: 0,
        hasContent: false,
        contentLength: 0
      };
    }

    const data = parseResult.data;
    const requiredCount = data.RequiredAttendees?.length || 0;
    const optionalCount = data.OptionalAttendees?.length || 0;
    const contentLength = parseResult.htmlContent?.length || 0;

    return {
      hasSubject: !!(data.Subject && data.Subject.trim().length > 0),
      requiredAttendeesCount: requiredCount,
      optionalAttendeesCount: optionalCount,
      totalAttendeesCount: requiredCount + optionalCount,
      hasContent: contentLength > 0,
      contentLength
    };
  }

  static isValidParseResult(parseResult: DocumentParseResult): boolean {
    if (!parseResult.success || !parseResult.data) {
      return false;
    }

    const data = parseResult.data;

    // At minimum, should have either a subject or some attendees
    const hasSubject = !!(data.Subject && data.Subject.trim().length > 0);
    const hasAttendees = (data.RequiredAttendees?.length || 0) > 0 || (data.OptionalAttendees?.length || 0) > 0;

    return hasSubject || hasAttendees;
  }

  /**
   * Convert a File to base64 data URL format
   * @param file - The image file to convert
   * @returns Promise<string> - Base64 data URL string (data:image/<type>;base64,<data>)
   */
  static convertFileToBase64DataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      // Validate that it's a supported image type
      const mimeType = file.type || 'image/png';
      if (!SUPPORTED_IMAGE_MIME_TYPES[mimeType]) {
        reject(new Error(`Unsupported image type: ${mimeType}. Supported types: ${Object.keys(SUPPORTED_IMAGE_MIME_TYPES).join(', ')}`));
        return;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          // FileReader.readAsDataURL returns format: data:<mimeType>;base64,<data>
          resolve(result);
        } else {
          reject(new Error('Failed to read file as base64 string'));
        }
      };

      reader.onerror = () => {
        reject(new Error(`Failed to read file: ${reader.error?.message || 'Unknown error'}`));
      };

      reader.onabort = () => {
        reject(new Error('File reading was aborted'));
      };

      // Read the file as a data URL (base64 encoded)
      reader.readAsDataURL(file);
    });
  }
}
